// src/views/login/Login.jsx
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { setToken } from "../../services/api";
import desktopImg from "../../assets/imgs/login/login.png";
import mobileImg from "../../assets/imgs/login/login.png";
import "./login.scss";

import { FaEnvelope, FaLock, FaArrowRight, FaSpinner } from "react-icons/fa";

const LOGIN_TIPO_ERROR = "Este acesso é exclusivo para instituições e professores.";
const TIPOS_PERMITIDOS = new Set(["professor", "instituicao"]);

function resolveDashboardPath(tipo) {
  if (tipo === "professor") return "/dashboard/professor";
  if (tipo === "instituicao") return "/dashboard/instituicao";
  return null;
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [erro, setErro] = useState("");
  const [loading, setLoading] = useState(false);

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 768 : true
  );

  const navigate = useNavigate();

  const hardLogout = () => {
    try {
      setToken(null);
      localStorage.removeItem("usuario");
      localStorage.removeItem("token");
    } catch { }
  };

  // ✅ valida sessão no backend antes de redirecionar
  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const t = localStorage.getItem("token");
        if (!t) return;

        const me = await api.get("/usuarios/me");
        if (!mounted) return;

        const tipo = me?.data?.tipo;
        const destino = resolveDashboardPath(tipo);

        // 🚫 Se logou com tipo inválido, não entra e mostra erro
        if (!TIPOS_PERMITIDOS.has(tipo) || !destino) {
          hardLogout();
          setErro(LOGIN_TIPO_ERROR);
          return;
        }

        // mantém o usuário atualizado no storage
        localStorage.setItem("usuario", JSON.stringify(me.data || {}));

        navigate(destino, { replace: true });
      } catch {
        hardLogout();
      }
    })();

    return () => {
      mounted = false;
    };
  }, [navigate]);

  // responsivo + iOS orientation
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (loading) return;

    setErro("");
    setLoading(true);

    try {
      const resp = await api.post("/auth/login", { email, senha });
      const { token } = resp.data || {};
      if (!token) throw new Error("Token ausente no login");

      setToken(token);

      const me = await api.get("/usuarios/me");
      const tipo = me?.data?.tipo;
      const destino = resolveDashboardPath(tipo);

      // 🚫 Bloqueio por tipo (somente professor/instituicao)
      if (!TIPOS_PERMITIDOS.has(tipo) || !destino) {
        hardLogout();
        setErro(LOGIN_TIPO_ERROR);
        return;
      }

      localStorage.setItem("usuario", JSON.stringify(me.data || {}));

      navigate(destino, { replace: true });
    } catch (err) {
      hardLogout();

      const msg =
        err?.response?.data?.erro ||
        "Credenciais inválidas. Verifique seus dados.";
      setErro(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="loginPage">
      <section
        className="loginWrap"
        role="dialog"
        aria-modal="true"
        aria-label="Tela de login"
      >
        <div className="loginCard">
          {/* Header */}
          <header className="loginHeader">
            <div
              className="brand"
              onClick={() => navigate("/")}
              role="button"
              tabIndex={0}
            >
              <div>
                <div className="brandName">iStudo</div>
                <div className="brandTag">Acesso institucional</div>
              </div>
            </div>

            <div className="title">
              <h1>Entrar</h1>
              <p>
                Gestores e professores: acesse seu painel e continue de onde
                parou.
              </p>
            </div>
          </header>

          {/* Content */}
          <div className="loginContent">
            {/* Image (some setups prefer to hide on mobile) */}
            <aside className="loginMedia" aria-hidden="true">
              <img src={isMobile ? mobileImg : desktopImg} alt="" />
            </aside>

            <div className="loginFormBox">
              <form className="loginForm" onSubmit={handleLogin}>
                <div className="field">
                  <label>Email</label>
                  <div className="inputWrap">
                    <span className="icon">
                      <FaEnvelope />
                    </span>
                    <input
                      type="email"
                      placeholder="seuemail@instituicao.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoComplete="username"
                      inputMode="email"
                    />
                  </div>
                </div>

                <div className="field">
                  <label>Senha</label>
                  <div className="inputWrap">
                    <span className="icon">
                      <FaLock />
                    </span>
                    <input
                      type="password"
                      placeholder="••••••••"
                      value={senha}
                      onChange={(e) => setSenha(e.target.value)}
                      required
                      autoComplete="current-password"
                    />
                  </div>
                </div>

                <div className="row">
                  <button
                    type="button"
                    className="linkBtn"
                    onClick={() => navigate("/recuperar-senha")}
                  >
                    Esqueceu a senha?
                  </button>
                </div>

                {erro && <div className="error">{erro}</div>}

                <button className="submit" type="submit" disabled={loading}>
                  {loading ? (
                    <>
                      <FaSpinner className="spin" /> Entrando...
                    </>
                  ) : (
                    <>
                      Entrar <FaArrowRight />
                    </>
                  )}
                </button>

                <div className="hint">
                  Sem acesso? Fale com o administrador da sua instituição.
                </div>

                <button
                  type="button"
                  className="ghost"
                  onClick={() => navigate("/")}
                >
                  Voltar para a home
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
